"use client";

import React, { useState, useEffect, useRef } from "react";
import { Star } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";

export default function Testimonials() {
  const { translate } = useLanguage();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCards, setVisibleCards] = useState(3);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);

  const sliderRef = useRef(null);

  /* ---------------- Fetch Reviews ---------------- */

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const response = await api.get("/reviews");

        let data = [];
        if (response.data?.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (Array.isArray(response.data)) {
          data = response.data;
        }

        setReviews(data);
      } catch (err) {
        console.error(err);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  /* ---------------- Responsive ---------------- */

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setVisibleCards(1);
      else if (window.innerWidth < 1024) setVisibleCards(2);
      else setVisibleCards(3);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------------- Prepare Infinite Slides ---------------- */

  const extendedReviews = [
    ...reviews.slice(-visibleCards),
    ...reviews,
    ...reviews.slice(0, visibleCards),
  ];

  useEffect(() => {
    if (reviews.length) {
      setCurrentIndex(visibleCards);
    }
  }, [reviews, visibleCards]);

  /* ---------------- Helpers ---------------- */

  const getInitials = (name = "") => {
    if (!name.trim()) return "??";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const renderStars = (rating = 0) => {
    const safe = Math.max(0, Math.min(5, Math.round(rating)));
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < safe ? "text-yellow-500 fill-yellow-500" : "text-gray-600"}`}
      />
    ));
  };

  /* ---------------- Navigation ---------------- */

  const nextSlide = () => {
    setCurrentIndex((prev) => prev + 1);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => prev - 1);
  };

  const handleTransitionEnd = () => {
    if (currentIndex >= reviews.length + visibleCards) {
      setIsTransitioning(false);
      setCurrentIndex(visibleCards);
    }

    if (currentIndex < visibleCards) {
      setIsTransitioning(false);
      setCurrentIndex(reviews.length + visibleCards - 1);
    }
  };

  useEffect(() => {
    if (!isTransitioning) {
      requestAnimationFrame(() => {
        setIsTransitioning(true);
      });
    }
  }, [isTransitioning]);

  /* ---------------- Loading ---------------- */

  if (loading) {
    return (
      <div className="py-32 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  if (!reviews.length) return null;
  console.log(extendedReviews, "addasd");

  return (
    <section className="py-20 bg-bg-light" dir="ltr">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-16" dir="rtl">
          <h2 className="text-4xl font-black text-white">
            {translate("newHome.testimonials.title") ||
              "What Our Customers Say"}
          </h2>
        </div>

        <div className="relative overflow-hidden">
          <div
            ref={sliderRef}
            onTransitionEnd={handleTransitionEnd}
            className={`flex ${
              isTransitioning
                ? "transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
                : ""
            }`}
            style={{
              transform: `translateX(-${currentIndex * (100 / visibleCards)}%)`,
            }}
          >
            {extendedReviews.map((review, index) => (
              <div
                key={index}
                className="flex-shrink-0 px-3"
                style={{ width: `${100 / visibleCards}%` }}
              >
                <div className="bg-[#13191f] border border-white/5 rounded-3xl p-8 flex flex-col h-full">
                  <div className="flex gap-1 mb-6">
                    {renderStars(review.rating)}
                  </div>

                  <p className="text-gray-400 mb-8 flex-grow">
                    "
                    {review.testimonial ||
                      review.comment ||
                      "No comment provided."}
                    "
                  </p>

                  <div className="flex items-center gap-4 pt-6 border-t border-white/5">
                    <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <span className="text-blue-400 font-bold">
                        {getInitials(review.name)}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-bold">
                        {review.name || "Anonymous User"}
                      </p>
                      <p className="text-gray-600 text-xs uppercase">
                        {review.location || "Verified Customer"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 bg-primary hover:bg-primary-dark text-white w-10 h-10 rounded-full"
          >
            ‹
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 bg-primary hover:bg-primary-dark text-white w-10 h-10 rounded-full"
          >
            ›
          </button>
        </div>
      </div>
    </section>
  );
}
